/**
 * Records (WALLIYO-0131): the HR side of the employee list, as the mockup
 * draws it — find, two narrowing chips, and a table of people.
 *
 * A row says who somebody is, what they do, whose work is theirs, when they
 * joined and when they were last here. It marks anybody holding something
 * granted by name with no end date, because that is the one thing on this
 * screen nobody is otherwise told about.
 */

import type { StaffMemberListItem } from "@wallyo/contracts/console";
import { t } from "../../i18n";
import { formatDateTime } from "../../i18n/format";
import { ErrorNotice } from "../../shell/ErrorNotice";
import { hrHref } from "./hr-route";
import { dueAReview, narrow, roleNames, teamNames, type RecordsFilters } from "./records-model";

export type RecordsState =
  | { status: "loading"; staff: readonly StaffMemberListItem[] | null }
  | { status: "ready"; staff: readonly StaffMemberListItem[] }
  | { status: "failed"; staff: readonly StaffMemberListItem[] | null; error: unknown };

/** A narrowing chip: the thing it narrows, and what it is set to. */
function Chip(props: {
  label: string;
  value: string;
  options: readonly string[];
  anyLabel: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className={props.value === "" ? "hr-chip" : "hr-chip on"}>
      <span className="foot">{props.label}</span>
      <select value={props.value} onChange={(event) => props.onChange(event.currentTarget.value)}>
        <option value="">{props.anyLabel}</option>
        {props.options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </label>
  );
}

function Teams({ member }: { member: StaffMemberListItem }) {
  if (member.teams.length === 0) return <span className="foot">{t("directory.noTeam")}</span>;
  return (
    <>
      {member.teams.map((team, index) => (
        <span key={team.staffTeamId}>
          {index > 0 ? ", " : ""}
          {team.name}
          {team.leads ? <span className="foot"> · {t("directory.leads")}</span> : null}
        </span>
      ))}
    </>
  );
}

export function RecordsView(props: {
  state: RecordsState;
  filters: RecordsFilters;
  onFilters: (filters: RecordsFilters) => void;
  onInvite: () => void;
  onOpen: (member: StaffMemberListItem) => void;
  onRetry: () => void;
}) {
  const { state, filters } = props;
  const staff = state.staff;
  const shown = staff ? narrow(staff, filters) : null;
  const flagged = staff ? dueAReview(staff) : 0;

  return (
    <>
      <div className="hr-search">
        <label className="field">
          <span className="label">{t("records.find")}</span>
          <input
            type="search"
            value={filters.query}
            placeholder={t("records.findPlaceholder")}
            onChange={(event) => props.onFilters({ ...filters, query: event.currentTarget.value })}
          />
        </label>
        <div className="grow" />
        <button className="btn" type="button" onClick={props.onInvite} disabled={state.status !== "ready"}>
          {t("joining.invite")}
        </button>
      </div>

      {staff ? (
        <div className="hr-chips">
          <Chip
            label={t("records.team")}
            value={filters.team}
            options={teamNames(staff)}
            anyLabel={t("records.any")}
            onChange={(team) => props.onFilters({ ...filters, team })}
          />
          <Chip
            label={t("records.role")}
            value={filters.role}
            options={roleNames(staff)}
            anyLabel={t("records.any")}
            onChange={(role) => props.onFilters({ ...filters, role })}
          />
          <div className="grow" />
          {flagged > 0 ? (
            <a className="hr-flag" href={hrHref({ tab: "employees", subTab: "reviews" })}>
              {t("records.dueReview", { count: String(flagged) })}
            </a>
          ) : null}
        </div>
      ) : null}

      {state.status === "failed" ? (
        <div className="audit-error">
          <p className="foot">{t("team.loadFailed")}</p>
          <ErrorNotice error={state.error} />
          <div className="btns">
            <button className="btn" type="button" onClick={props.onRetry}>
              {t("team.retry")}
            </button>
          </div>
        </div>
      ) : null}

      {!staff && state.status === "loading" ? (
        <div className="empty" role="status">
          <span className="spinner" aria-hidden="true" />
          <span>{t("team.loading")}</span>
        </div>
      ) : null}

      {shown && shown.length === 0 ? <div className="empty">{t("records.none")}</div> : null}

      {shown && shown.length > 0 ? (
        <section className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t("records.col.person")}</th>
                  <th>{t("records.col.role")}</th>
                  <th>{t("records.col.teams")}</th>
                  <th>{t("records.col.joined")}</th>
                  <th>{t("records.col.lastSeen")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {shown.map((member) => (
                  <tr key={member.staffMemberId}>
                    <td>
                      <strong>{member.name}</strong>
                      {member.openGrants > 0 ? (
                        <>
                          {" "}
                          <span className="pill warn">{t("records.accessDueReview")}</span>
                        </>
                      ) : null}
                      {member.status !== "active" ? (
                        <>
                          {" "}
                          <span className="pill">
                            {member.status === "invited" ? t("directory.invited") : t("leaving.alreadyLeft")}
                          </span>
                        </>
                      ) : null}
                      <div className="foot">{member.email}</div>
                    </td>
                    <td>
                      {member.roles.length > 0 ? (
                        member.roles.map((role) => role.name).join(", ")
                      ) : (
                        <span className="foot">{t("directory.noRole")}</span>
                      )}
                    </td>
                    <td>
                      <Teams member={member} />
                    </td>
                    <td>{formatDateTime(member.createdAt)}</td>
                    <td>
                      {member.lastSeenAt ? (
                        formatDateTime(member.lastSeenAt)
                      ) : (
                        <span className="foot">{t("records.neverSeen")}</span>
                      )}
                    </td>
                    <td className="num">
                      <button className="btn quiet small" type="button" onClick={() => props.onOpen(member)}>
                        {t("directory.open")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </>
  );
}
